/**
 * Early Bird Collection Integration Script
 *
 * This script enhances Early Bird app functionality in collection pages by:
 * 1. Injecting "Back-order" badges into product cards
 * 2. Converting Early Bird "View product" buttons to functional "Add to cart" buttons
 * 3. Ensuring proper form setup and submission handling for back-order products
 *
 * The script waits for Early Bird app processing to complete before making changes,
 * and operates within both featured collection sections and main collection pages.
 *
 * @author Toucan Cocktails Development Team
 * @version 2.1.0
 */

// Configuration constants
const EARLYBIRD_CONFIG = {
  SELECTORS: {
    FEATURED_COLLECTION: ".collection[data-id]",
    MAIN_COLLECTION: "#product-grid[data-id]",
    COLLECTION_ITEM: "li.earlybird__collection_item",
    CARD_MEDIA: ".card__media",
    EARLYBIRD_BUTTON: ".earlybird__button",
    EARLYBIRD_BADGE: ".earlybird__badge",
    SELLING_PLAN_WRAPPER: ".earlybird__selling_plan_wrapper",
    COLLECTION_BUTTON: ".earlybird-collection-button",
  },
  CLASSES: {
    BADGE: "earlybird__badge",
    COLLECTION_BUTTON: "earlybird-collection-button",
    EARLYBIRD_BUTTON: "earlybird__button",
    DISABLED_BUTTON: "earlybird__button--disabled",
  },
  TIMING: {
    MAX_WAIT: 5000,
    CHECK_INTERVAL: 100,
    COMPLETION_DELAY: 200,
  },
};

class EarlybirdInjector {
  constructor() {
    this.collectionItems = [];
    this.featuredCollectionSections = [];
    this.mainCollectionSections = [];
    this.init();
  }

  // ========================================
  // INITIALIZATION & SETUP
  // ========================================

  /**
   * Initialize the injector and wait for Early Bird processing
   */
  async init() {
    this.findAllCollectionSections();
    await this.waitForEarlyBirdProcessing();
    this.findCollectionItems();
    this.setupObserver();
    this.processExistingItems();
  }

  /**
   * Wait for Early Bird app to finish processing
   * @returns {Promise} Resolves when processing is complete
   */
  waitForEarlyBirdProcessing() {
    return new Promise((resolve) => {
      let elapsed = 0;

      const checkProcessing = () => {
        // Check if Early Bird has processed by looking for their classes/elements
        const hasEarlybirdCollection = document.querySelector(
          ".earlybird__collection"
        );
        const hasEarlybirdButtons = document.querySelector(
          EARLYBIRD_CONFIG.SELECTORS.EARLYBIRD_BUTTON
        );
        const hasBodyClass =
          document.body.classList.contains("earlybird__valid");

        if (
          hasEarlybirdCollection ||
          hasEarlybirdButtons ||
          hasBodyClass ||
          elapsed >= EARLYBIRD_CONFIG.TIMING.MAX_WAIT
        ) {
          console.log("Early Bird processing detected or timeout reached");
          setTimeout(resolve, EARLYBIRD_CONFIG.TIMING.COMPLETION_DELAY);
        } else {
          elapsed += EARLYBIRD_CONFIG.TIMING.CHECK_INTERVAL;
          setTimeout(checkProcessing, EARLYBIRD_CONFIG.TIMING.CHECK_INTERVAL);
        }
      };

      checkProcessing();
    });
  }

  // ========================================
  // ELEMENT DISCOVERY & SETUP
  // ========================================

  /**
   * Find all collection sections (both featured and main)
   */
  findAllCollectionSections() {
    this.findFeaturedCollectionSections();
    this.findMainCollectionSections();
  }

  /**
   * Find all featured-collection sections
   */
  findFeaturedCollectionSections() {
    this.featuredCollectionSections = document.querySelectorAll(
      EARLYBIRD_CONFIG.SELECTORS.FEATURED_COLLECTION
    );
    console.log(
      `Found ${this.featuredCollectionSections.length} featured collection sections`
    );
  }

  /**
   * Find all main collection product grid sections
   */
  findMainCollectionSections() {
    this.mainCollectionSections = document.querySelectorAll(
      EARLYBIRD_CONFIG.SELECTORS.MAIN_COLLECTION
    );
    console.log(
      `Found ${this.mainCollectionSections.length} main collection sections`
    );
  }

  /**
   * Find all collection items with the earlybird class within any collection section
   */
  findCollectionItems() {
    const featuredSelector = `${EARLYBIRD_CONFIG.SELECTORS.FEATURED_COLLECTION} ${EARLYBIRD_CONFIG.SELECTORS.COLLECTION_ITEM}`;
    const mainSelector = `${EARLYBIRD_CONFIG.SELECTORS.MAIN_COLLECTION} ${EARLYBIRD_CONFIG.SELECTORS.COLLECTION_ITEM}`;
    
    const featuredItems = document.querySelectorAll(featuredSelector);
    const mainItems = document.querySelectorAll(mainSelector);
    
    this.collectionItems = [...featuredItems, ...mainItems];
    console.log(
      `Found ${this.collectionItems.length} earlybird collection items (${featuredItems.length} featured + ${mainItems.length} main)`
    );
  }

  /**
   * Setup mutation observer to handle dynamically added elements and class changes
   * Observes both featured and main collection sections
   */
  setupObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node is within any collection section
              const isInCollection =
                (node.closest && node.closest(".collection[data-id]")) ||
                (node.closest && node.closest("#product-grid[data-id]"));

              if (isInCollection) {
                // Check if the added node is an earlybird collection item
                if (
                  node.matches &&
                  node.matches("li.earlybird__collection_item")
                ) {
                  this.processCollectionItem(node);
                }
                // Check if the added node contains earlybird collection items
                const nestedItems =
                  node.querySelectorAll &&
                  node.querySelectorAll("li.earlybird__collection_item");
                if (nestedItems && nestedItems.length > 0) {
                  nestedItems.forEach((item) =>
                    this.processCollectionItem(item)
                  );
                }
              }
            }
          });
        }

        // Watch for class attribute changes (when another script adds earlybird classes)
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          const target = mutation.target;
          if (
            target.matches &&
            target.matches("li.earlybird__collection_item")
          ) {
            // Class was added to an li element, check if it needs processing
            const isInCollection = 
              target.closest(".collection[data-id]") ||
              target.closest("#product-grid[data-id]");
            if (isInCollection) {
              this.processCollectionItem(target);
            }
          }
        }

        // Watch for style attribute changes on earlybird badges
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style"
        ) {
          const target = mutation.target;
          if (target.matches && target.matches(".earlybird__badge")) {
            // Style was changed on an earlybird badge - could be from the app
            console.log(
              "Earlybird badge style updated by external script:",
              target
            );
            // The badge styling is now controlled by the app, so we don't need to override it
          }
        }

        // Watch for data attribute changes that might indicate app updates
        if (
          mutation.type === "attributes" &&
          mutation.attributeName &&
          mutation.attributeName.startsWith("data-")
        ) {
          const target = mutation.target;
          if (
            target.closest &&
            target.closest("li.earlybird__collection_item")
          ) {
            // Data attribute changed on earlybird item - might need reprocessing
            const collectionItem = target.closest(
              "li.earlybird__collection_item"
            );
            const isInCollection = 
              collectionItem.closest(".collection[data-id]") ||
              collectionItem.closest("#product-grid[data-id]");
            if (isInCollection) {
              // Check if we need to update the badge based on new data
              this.updateBadgeIfNeeded(collectionItem);
            }
          }
        }
      });
    });

    // Observe both featured and main collection sections
    const allSections = [
      ...this.featuredCollectionSections,
      ...this.mainCollectionSections,
    ];
    
    allSections.forEach((section) => {
      observer.observe(section, {
        childList: true,
        subtree: true,
        attributes: true, // Watch for attribute changes
        attributeFilter: ["class", "style", "data-earlybird", "data-backorder"], // Watch class, style, and relevant data attributes
      });
    });
    
    console.log(`Observing ${allSections.length} collection sections for changes`);
  }

  // ========================================
  // COLLECTION ITEM PROCESSING
  // ========================================

  /**
   * Process existing collection items on page load
   */
  processExistingItems() {
    this.collectionItems.forEach((item) => {
      this.processCollectionItem(item);
    });
  }

  /**
   * Process a single collection item
   * @param {HTMLElement} collectionItem - The li element with earlybird__collection_item class
   */
  processCollectionItem(collectionItem) {
    // Check if this item has already been processed to prevent duplicates
    if (collectionItem.dataset.earlybirdProcessed) {
      console.log(
        "Collection item already processed, skipping:",
        collectionItem
      );
      return;
    }

    const targetElement = this.findTargetElement(collectionItem);

    if (targetElement) {
      this.injectElement(targetElement, collectionItem);
    } else {
      console.warn(
        "Target element not found within collection item:",
        collectionItem
      );
    }

    // Restore Quick Add functionality after Early Bird processes
    this.restoreQuickAddButtons(collectionItem);

    // Mark this item as processed
    collectionItem.dataset.earlybirdProcessed = "true";
    console.log("Marked collection item as processed:", collectionItem);
  }

  /**
   * Replace Early Bird's "View product" buttons with functional "Add to cart" buttons
   * @param {HTMLElement} collectionItem - The collection item to process
   */
  restoreQuickAddButtons(collectionItem) {
    // Find Early Bird's "View product" buttons
    const earlybirdButtons =
      collectionItem.querySelectorAll(".earlybird__button");

    earlybirdButtons.forEach((earlybirdButton) => {
      // Check if this is a "View product" button (not other Early Bird buttons like BIS)
      const buttonText = earlybirdButton.textContent.trim().toLowerCase();
      const isViewProductButton =
        buttonText.includes("view product") ||
        buttonText.includes("view") ||
        (earlybirdButton.onclick &&
          earlybirdButton.onclick.toString().includes("window.location.href"));

      if (isViewProductButton) {
        console.log(
          "Replacing Early Bird 'View product' button with product page style button:",
          earlybirdButton
        );

        // Hide the "View product" button
        earlybirdButton.style.display = "none";

        // Create a new button using Early Bird's product page approach
        this.createEarlybirdProductPageButton(collectionItem, earlybirdButton);
      }
    });
  }

  // ========================================
  // EARLY BIRD BUTTON REPLACEMENT
  // ========================================

  /**
   * Create Early Bird product page style button with full form setup
   * Replicates the exact button and form setup from Early Bird's product page
   * @param {HTMLElement} collectionItem - The collection item
   * @param {HTMLElement} originalButton - The original Early Bird button for reference
   */
  createEarlybirdProductPageButton(collectionItem, originalButton) {
    // Check if we've already created a button for this collection item
    if (
      collectionItem.querySelector(EARLYBIRD_CONFIG.SELECTORS.COLLECTION_BUTTON)
    ) {
      console.log(
        "Early Bird button already exists for this collection item, skipping"
      );
      return;
    }

    // Get product information from the collection item
    const productLink = collectionItem.querySelector('a[href*="/products/"]');
    if (!productLink) {
      console.warn("No product link found in collection item");
      return;
    }

    const productHandle = productLink.href.split("/products/")[1].split("?")[0];
    const form = originalButton.form || originalButton.closest("form");

    if (!form) {
      console.warn("No form found for Early Bird button");
      return;
    }

    console.log(
      "Creating Early Bird product page style form for product:",
      productHandle
    );

    // Create the form elements that Early Bird creates on product pages
    this.createEarlybirdFormElements(form, collectionItem);

    // Create the new add to cart button (same as Early Bird product page)
    const newButton = this.createEarlybirdAddToCartButton(originalButton, form);

    // Insert the new button in place of the original
    originalButton.parentNode.insertBefore(newButton, originalButton);

    console.log("Created Early Bird product page style button:", newButton);
  }

  /**
   * Create Early Bird form elements (same as product page)
   * @param {HTMLElement} form - The form element
   * @param {HTMLElement} collectionItem - The collection item
   */
  createEarlybirdFormElements(form, collectionItem) {
    // Check if Early Bird properties already exist
    let earlybirdProperty = form.querySelector(
      'input[name="properties[_EarlyBird]"]'
    );
    let campaignProperty = form.querySelector(
      'input[name="properties[_EarlyBirdCampaign]"]'
    );
    let purchaseOptionProperty = form.querySelector(
      'input[name="properties[Purchase option]"]'
    );

    // Create Early Bird line property (same as product page)
    if (!earlybirdProperty) {
      earlybirdProperty = document.createElement("input");
      earlybirdProperty.type = "hidden";
      earlybirdProperty.name = "properties[_EarlyBird]";
      earlybirdProperty.value = "Back-order"; // or get from Early Bird global settings
      form.appendChild(earlybirdProperty);
      console.log("Created _EarlyBird property");
    }

    // Create campaign property if available from Early Bird global
    if (!campaignProperty && window.EBGlobal?.campaign?.uuid) {
      campaignProperty = document.createElement("input");
      campaignProperty.type = "hidden";
      campaignProperty.name = "properties[_EarlyBirdCampaign]";
      campaignProperty.value = window.EBGlobal.campaign.uuid;
      form.appendChild(campaignProperty);
      console.log("Created _EarlyBirdCampaign property");
    }

    // Create public purchase option property (same as product page)
    if (!purchaseOptionProperty) {
      const purchaseOptionLabel =
        window.EBGlobal?.localisation?.purchaseOptionLabel || "Purchase option";
      const backorderLabel =
        window.EBGlobal?.localisation?.backorderLabel || "Back-order";

      purchaseOptionProperty = document.createElement("input");
      purchaseOptionProperty.type = "hidden";
      purchaseOptionProperty.name = `properties[${purchaseOptionLabel}]`;
      purchaseOptionProperty.value = backorderLabel;
      form.appendChild(purchaseOptionProperty);
      console.log("Created purchase option property");
    }

    // Handle selling plans if they exist
    this.handleSellingPlans(form, collectionItem);
  }

  /**
   * Handle selling plans (same logic as Early Bird product page)
   * @param {HTMLElement} form - The form element
   * @param {HTMLElement} collectionItem - The collection item
   */
  handleSellingPlans(form, collectionItem) {
    // Look for selling plan options
    const sellingPlanWrapper = document.querySelector(
      ".earlybird__selling_plan_wrapper"
    );

    if (sellingPlanWrapper) {
      const sellingPlanRadios = sellingPlanWrapper.querySelectorAll(
        'input[name="selling_plan"]'
      );

      if (sellingPlanRadios.length > 0) {
        // Find selected or select default (same as product page logic)
        let selectedRadio = sellingPlanWrapper.querySelector(
          'input[name="selling_plan"]:checked'
        );

        if (!selectedRadio) {
          // Auto-select full payment option or first option
          selectedRadio =
            sellingPlanWrapper.querySelector(
              'input[name="selling_plan"][data-type="full"]'
            ) || sellingPlanRadios[0];

          if (selectedRadio) {
            selectedRadio.checked = true;
            selectedRadio.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }

        // Ensure selling plan value is in form
        if (selectedRadio) {
          let sellingPlanInput = form.querySelector(
            'input[name="selling_plan"]'
          );
          if (!sellingPlanInput) {
            sellingPlanInput = document.createElement("input");
            sellingPlanInput.type = "hidden";
            sellingPlanInput.name = "selling_plan";
            form.appendChild(sellingPlanInput);
          }
          sellingPlanInput.value = selectedRadio.value;
          console.log("Set selling plan in form:", selectedRadio.value);
        }
      }
    }
  }

  /**
   * Create Early Bird add to cart button (same as product page)
   * @param {HTMLElement} originalButton - Original button for styling reference
   * @param {HTMLElement} form - The form element
   * @returns {HTMLElement} The new button
   */
  createEarlybirdAddToCartButton(originalButton, form) {
    const button = document.createElement("button");

    // Copy classes from original button but add Early Bird classes
    button.classList = originalButton.classList;
    button.classList.add(EARLYBIRD_CONFIG.CLASSES.EARLYBIRD_BUTTON);
    button.classList.add(EARLYBIRD_CONFIG.CLASSES.COLLECTION_BUTTON);
    button.classList.remove(EARLYBIRD_CONFIG.CLASSES.DISABLED_BUTTON);

    // Set button properties
    button.type = "submit";
    button.textContent = this.getButtonText();

    // Apply Early Bird styling
    this.applyButtonStyling(button);

    // Copy form reference if original button had one
    if (originalButton.getAttribute("form")) {
      button.setAttribute("form", originalButton.getAttribute("form"));
    }

    // Add submit handler
    this.addProductPageSubmitHandler(form, button);

    return button;
  }

  /**
   * Get button text from Early Bird configuration
   * @returns {string} Button text
   */
  getButtonText() {
    return (
      window.EBGlobal?.widget?.backorderAddToCartButtonText ||
      window.EBGlobal?.localisation?.quickAddButtonCta ||
      "Add back-order to cart"
    );
  }

  /**
   * Apply Early Bird styling to button
   * @param {HTMLElement} button - Button element to style
   */
  applyButtonStyling(button) {
    const widget = window.EBGlobal?.widget;
    if (widget?.backorderAddToCartButtonTextColor) {
      button.style.color = widget.backorderAddToCartButtonTextColor;
    }
    if (widget?.backorderAddToCartButtonBackgroundColor) {
      button.style.backgroundColor =
        widget.backorderAddToCartButtonBackgroundColor;
      button.style.borderWidth = "0px";
    }
  }

  // ========================================
  // FORM SUBMISSION HANDLING
  // ========================================

  /**
   * Ensure selling plan is properly included in form
   * @param {HTMLElement} form - The form element
   * @param {string} sellingPlanId - The selling plan ID
   */
  ensureSellingPlanInForm(form, sellingPlanId) {
    let sellingPlanInput = form.querySelector('input[name="selling_plan"]');

    if (!sellingPlanInput) {
      // Create hidden input for selling plan if it doesn't exist
      sellingPlanInput = document.createElement("input");
      sellingPlanInput.type = "hidden";
      sellingPlanInput.name = "selling_plan";
      form.appendChild(sellingPlanInput);
      console.log("Created selling_plan hidden input");
    }

    sellingPlanInput.value = sellingPlanId;
    console.log("Selling plan set in form:", sellingPlanId);
  }

  /**
   * Add product page style submit handler to ensure proper Early Bird submission
   * Based on Early Bird product page logic
   * @param {HTMLElement} form - The form element
   * @param {HTMLElement} button - The submit button
   */
  addProductPageSubmitHandler(form, button) {
    // Prevent duplicate handlers
    if (form.dataset.earlybirdProductPageHandlerAdded) {
      return;
    }

    form.addEventListener("submit", (event) => {
      console.log("Early Bird product page style form submit intercepted");

      // Get selected selling plan
      const selectedRadio =
        form.querySelector('input[name="selling_plan"]:checked') ||
        document.querySelector('input[name="selling_plan"]:checked');

      if (selectedRadio) {
        // Ensure selling plan is in the form
        this.ensureSellingPlanInForm(form, selectedRadio.value);
      }

      // Ensure all Early Bird properties are present
      this.ensureEarlybirdProperties(form);

      // Log form data for debugging
      const formData = new FormData(form);
      console.log("Early Bird product page style form submission data:");
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}: ${value}`);
      }

      // Let the form submit normally with all Early Bird properties
    });

    form.dataset.earlybirdProductPageHandlerAdded = "true";
    console.log("Added Early Bird product page style submit handler");
  }

  /**
   * Ensure all required Early Bird properties are in the form
   * @param {HTMLElement} form - The form element
   */
  ensureEarlybirdProperties(form) {
    // Check and create _EarlyBird property
    if (!form.querySelector('input[name="properties[_EarlyBird]"]')) {
      const earlybirdProperty = document.createElement("input");
      earlybirdProperty.type = "hidden";
      earlybirdProperty.name = "properties[_EarlyBird]";
      earlybirdProperty.value =
        window.EBGlobal?.localisation?.backorderLabel || "Back-order";
      form.appendChild(earlybirdProperty);
      console.log("Added missing _EarlyBird property");
    }

    // Check and create purchase option property
    const purchaseOptionLabel =
      window.EBGlobal?.localisation?.purchaseOptionLabel || "Purchase option";
    if (
      !form.querySelector(`input[name="properties[${purchaseOptionLabel}]"]`)
    ) {
      const purchaseOptionProperty = document.createElement("input");
      purchaseOptionProperty.type = "hidden";
      purchaseOptionProperty.name = `properties[${purchaseOptionLabel}]`;
      purchaseOptionProperty.value =
        window.EBGlobal?.localisation?.backorderLabel || "Back-order";
      form.appendChild(purchaseOptionProperty);
      console.log("Added missing purchase option property");
    }

    // Check and create campaign property if available
    if (
      window.EBGlobal?.campaign?.uuid &&
      !form.querySelector('input[name="properties[_EarlyBirdCampaign]"]')
    ) {
      const campaignProperty = document.createElement("input");
      campaignProperty.type = "hidden";
      campaignProperty.name = "properties[_EarlyBirdCampaign]";
      campaignProperty.value = window.EBGlobal.campaign.uuid;
      form.appendChild(campaignProperty);
      console.log("Added missing _EarlyBirdCampaign property");
    }
  }

  // ========================================
  // BADGE INJECTION
  // ========================================

  /**
   * Find the target element for badge injection within a collection item
   * @param {HTMLElement} collectionItem - The collection item to search within
   * @returns {HTMLElement|null} The target element or null if not found
   */
  findTargetElement(collectionItem) {
    const target = collectionItem.querySelector(
      EARLYBIRD_CONFIG.SELECTORS.CARD_MEDIA
    );
    if (!target) {
      console.warn(
        "Card media element not found in collection item:",
        collectionItem
      );
    }
    return target;
  }

  /**
   * Create the earlybird badge element
   * @returns {HTMLElement} The badge element to inject
   */
  createBadgeElement() {
    const badge = document.createElement("div");
    badge.className = EARLYBIRD_CONFIG.CLASSES.BADGE;
    badge.style.backgroundColor = "rgb(221, 82, 85)";
    badge.textContent = "Back-order";
    return badge;
  }

  /**
   * Inject the earlybird badge into the target element
   * @param {HTMLElement} targetElement - The target element for badge injection
   * @param {HTMLElement} collectionItem - The parent collection item
   */
  injectElement(targetElement, collectionItem) {
    // Check if badge is already injected to avoid duplicates
    if (
      targetElement.querySelector(EARLYBIRD_CONFIG.SELECTORS.EARLYBIRD_BADGE)
    ) {
      return;
    }

    const badge = this.createBadgeElement();
    targetElement.appendChild(badge);
    console.log("Earlybird badge injected:", targetElement);
  }

  /**
   * Update badge if needed based on data attribute changes
   * @param {HTMLElement} collectionItem - The collection item that had data changes
   */
  updateBadgeIfNeeded(collectionItem) {
    const targetElement = this.findTargetElement(collectionItem);
    const existingBadge = targetElement?.querySelector(".earlybird__badge");

    if (existingBadge && targetElement) {
      console.log(
        "Data attribute changed, updating badge for:",
        collectionItem
      );
      // Remove existing badge and create new one with updated data
      existingBadge.remove();
      this.injectElement(targetElement, collectionItem);
    }
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Refresh and reprocess all items
   */
  refresh() {
    // Reset processed flags
    const processedItems = document.querySelectorAll(
      "[data-earlybird-processed]"
    );
    processedItems.forEach((item) => {
      delete item.dataset.earlybirdProcessed;
    });

    this.findAllCollectionSections();
    this.findCollectionItems();
    this.processExistingItems();
  }

  /**
   * Get section ID from a collection item
   * @param {HTMLElement} collectionItem - The collection item
   * @returns {string|null} The section ID or null if not found
   */
  getSectionId(collectionItem) {
    const section = collectionItem.closest(".collection[data-id]") ||
                   collectionItem.closest("#product-grid[data-id]");
    return section ? section.getAttribute("data-id") : null;
  }

  /**
   * Check if this is within any collection section
   * @param {HTMLElement} collectionItem - The collection item
   * @returns {boolean} True if it's within any collection
   */
  isInAnyCollection(collectionItem) {
    return Boolean(
      collectionItem.closest(".collection[data-id]") ||
      collectionItem.closest("#product-grid[data-id]")
    );
  }
}

// ========================================
// INITIALIZATION
// ========================================

/**
 * Initialize the EarlybirdInjector when DOM is ready
 */
function initializeEarlybirdInjector() {
  if (!window.earlybirdInjector) {
    window.earlybirdInjector = new EarlybirdInjector();
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", initializeEarlybirdInjector);

// Also initialize immediately if DOM is already loaded
if (document.readyState !== "loading") {
  initializeEarlybirdInjector();
}

// ========================================
// GLOBAL API
// ========================================

/**
 * Expose refresh method for manual triggering
 * Usage: window.earlybirdRefresh() in console or other scripts
 */
window.earlybirdRefresh = () => {
  if (window.earlybirdInjector) {
    window.earlybirdInjector.refresh();
  } else {
    console.warn("EarlybirdInjector not initialized yet");
  }
};
